/**
 * PlayErrorBoundary — Susurro's render safety net.
 *
 * A failure inside a screen must NOT leave the view blank: we catch it
 * with a Solid `<ErrorBoundary>` and show a calm, theme-aware fallback
 * (resting whisper-wave illustration + calm message + clear actions).
 * The user can retry (resets the subtree) or go back home.
 *
 * Theme-aware: uses brand tokens (surface/foreground/accent/success), respects
 * the global reduced-motion guard. Logs the error to the console for diagnosis.
 */

import { type Component, ErrorBoundary, type JSX } from "solid-js";
import { Home, RotateCcw } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";

interface PlayErrorBoundaryProps {
  children: JSX.Element;
  /** Identifies the subtree that failed (route/screen) for the log. */
  scope?: string;
}

/** Resting whisper wave (inline SVG, theme-aware via brand tokens). */
function WhisperGlyph() {
  return (
    <svg viewBox="0 0 120 120" class="h-28 w-28" role="img" aria-hidden="true" fill="none">
      <circle cx="60" cy="60" r="46" fill="var(--surface-secondary)" />
      <circle cx="60" cy="60" r="46" stroke="var(--glass-border)" stroke-width="1.5" />
      <g stroke="var(--accent)" stroke-width="5" stroke-linecap="round" opacity="0.9">
        <line x1="40" y1="54" x2="40" y2="66" />
        <line x1="52" y1="46" x2="52" y2="74" />
        <line x1="64" y1="40" x2="64" y2="80" />
        <line x1="76" y1="49" x2="76" y2="71" />
      </g>
      <g transform="translate(84 82)">
        <circle r="14" fill="var(--success)" />
        <path
          d="M-4.5 -1 v-2.6 a4.5 4.5 0 0 1 9 0 v2.6"
          fill="none"
          stroke="var(--surface)"
          stroke-width="2.2"
          stroke-linecap="round"
        />
        <rect x="-6" y="-1" width="12" height="9" rx="2.4" fill="var(--surface)" />
      </g>
    </svg>
  );
}

export const PlayErrorBoundary: Component<PlayErrorBoundaryProps> = (props) => {
  const { t } = useI18n();
  return (
    <ErrorBoundary
      fallback={(error, reset) => {
        console.error(`[PlayErrorBoundary${props.scope ? `:${props.scope}` : ""}]`, error);
        return (
          <div class="flex min-h-dvh flex-col items-center justify-center gap-6 bg-background p-6 text-center select-none">
            <div class="animate-scale-in">
              <WhisperGlyph />
            </div>
            <div class="space-y-2">
              <h2 class="text-2xl font-bold tracking-tight text-foreground">
                {t(msg`Something went wrong`)}
              </h2>
              <p class="mx-auto max-w-sm text-sm text-muted-foreground">
                {t(msg`An unexpected error has occurred.`)}
              </p>
            </div>
            <div class="flex flex-col items-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => {
                  reset();
                }}
                class="flex min-h-11 items-center gap-2 rounded-full bg-primary px-6 font-semibold text-primary-foreground shadow-md transition-transform hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none active:scale-[0.98]"
              >
                <RotateCcw class="h-5 w-5" aria-hidden="true" />
                {t(msg`Try again`)}
              </button>
              <a
                href="/"
                class="flex min-h-11 items-center gap-2 rounded-full border border-[var(--border)] bg-[var(--surface)] px-6 font-semibold text-foreground transition-transform hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none active:scale-[0.98]"
              >
                <Home class="h-5 w-5" aria-hidden="true" />
                {t(msg`Home`)}
              </a>
            </div>
          </div>
        );
      }}
    >
      {props.children}
    </ErrorBoundary>
  );
};
